import { useState, useEffect, useRef, useCallback } from "react";
import {
  subscribeToAddress,
  subscribeToRecord,
  checkImageExists,
  writeAddedToMauticFalse,
  getTempRecord
} from "../services/addressService";

export function useAddressEditor(userID, uniqueKey, onDone) {
  const [loading, setLoading] = useState(true);
  const [originalAddress, setOriginalAddress] = useState("");
  const [editedAddress, setEditedAddress] = useState("");
  const [status, setStatus] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [isDeleted, setIsDeleted] = useState(false);
  const statusRef = useRef(status);

  useEffect(() => { statusRef.current = status; }, [status]);

  const finishSave = useCallback(async () => {
    setStatus("saved");
    try {
      const hasImage = await checkImageExists(userID, uniqueKey);
      setTimeout(() => onDone?.(hasImage), 600);
    } catch {
      setStatus("error");
      setValidationError("Failed to verify image status.");
    }
  }, [userID, uniqueKey, onDone]);

  // Subscribe to address value
  useEffect(() => {
    return subscribeToAddress(userID, uniqueKey, (val) => {
      if (val) {
        setOriginalAddress(val);
        setEditedAddress((prev) => (prev?.length ? prev : val));
        setLoading(false);
      } else if (["validating", "saving", "error"].includes(statusRef.current)) {
        setLoading(false);
      } else {
        setLoading(true);
        setOriginalAddress("");
      }
    });
  }, [userID, uniqueKey]);

  // Subscribe to validation status / parent record
  useEffect(() => {
    const BUSY_STATUSES = ["saving", "validating"];
    if (!BUSY_STATUSES.includes(status) && !isDeleted) return;

    return subscribeToRecord(userID, uniqueKey, async (data) => {
      if (!data) {
        setIsDeleted(true);
        setStatus("error");
        
        try {
          const tempData = await getTempRecord(userID, uniqueKey);
          if (tempData && (tempData.uspsValidationError || tempData.error)) {
            setValidationError(tempData.uspsValidationError || tempData.error);
            return;
          }
          setValidationError("Scan rejected or deleted during validation.");
        } catch (err) {
          console.error("Error fetching temp data:", err);
          setValidationError("Validation failed and record was removed.");
        }
        return;
      }

      if (data.uspsValidationPending === true) {
        return;
      }

      if (data.uspsValidationError) {
        setStatus("error");
        setValidationError(data.uspsValidationError);
        return;
      }

      if (data.duplicateAddress === true) {
        setStatus("error");
        setValidationError("This address has already been scanned for this campaign.");
        return;
      }

      if (data.uspsValidationPending === false || data.duplicateAddressDifferentCampaign === true) {
        finishSave();
      }
    });
  }, [userID, uniqueKey, status, isDeleted, finishSave]);

  const handleSave = async () => {
    setStatus("saving");
    setValidationError(null);

    try {
      if (!editedAddress || editedAddress === originalAddress) {
        await writeAddedToMauticFalse(userID, uniqueKey, {});
      } else {
        await writeAddedToMauticFalse(userID, uniqueKey, { editedAddress });
      }
      setStatus("validating");
    } catch (err) {
      console.error("Failed to save:", err);
      setStatus("error");
      setValidationError("Failed to save address. Please try again.");
    }
  };

  return {
    loading,
    originalAddress,
    editedAddress,
    setEditedAddress,
    status,
    validationError,
    isDeleted,
    handleSave,
    finishSave,
  };
}
